/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { FunctionComponent } from "react";
import { useEffect } from "react";

import {
  selectAlertOverlaySelectedActionId,
  selectDismissedByActionId,
  setRetryPending
} from "../../viewModel/alertOverlay";
import {
  CONNECTION_EXIT_ACTION_ID,
  CONNECTION_RETRY_ACTION_ID,
  exitApplicationFromConnectionOverlay,
  triggerManualRetry
} from "../../viewModel/connectionRetry";
import {
  cancelSignInSession,
  SIGN_IN_AGAIN_RETRY_ACTION_ID,
  stopDbsPolling
} from "../../viewModel/signIn";
import { useUnityDispatch, useUnitySelector } from "../../viewModel";

/**
 * Headless glue between the shared alert overlay and the connection-retry
 * machinery. Mounted once at the app root so it serves every CRITICAL
 * "Connection Error" overlay — sign-in, app startup and post-sign-in startup.
 *
 * Retry keeps the overlay open, so it arrives as a selected action: mark the
 * button pending and wake the CRITICAL loop to re-issue the failed call now.
 * Exit dismisses, so it arrives as a dismissal: tear down any background retry
 * and close the app through the existing platform abstraction (TV only — the
 * button is never rendered in the browser). Sign In Again (sign-in overlay
 * only) dismisses too: end the attempt in flight and return to the form.
 */
const ConnectionRetryOverlayBridge: FunctionComponent = (): null => {
  const dispatch = useUnityDispatch();
  const selectedActionId: string = useUnitySelector(
    selectAlertOverlaySelectedActionId
  );
  const dismissedByActionId: string = useUnitySelector(
    selectDismissedByActionId
  );

  useEffect((): void => {
    if (selectedActionId === CONNECTION_RETRY_ACTION_ID) {
      dispatch(setRetryPending(true));
      triggerManualRetry();
    }
  }, [selectedActionId, dispatch]);

  useEffect((): void => {
    if (dismissedByActionId === CONNECTION_EXIT_ACTION_ID) {
      stopDbsPolling();
      exitApplicationFromConnectionOverlay();
    }
    // Sign-in overlay only: end the retrying attempt so the credential form
    // underneath is usable again.
    if (dismissedByActionId === SIGN_IN_AGAIN_RETRY_ACTION_ID) {
      cancelSignInSession();
    }
  }, [dismissedByActionId]);

  return null;
};

export default ConnectionRetryOverlayBridge;
